import { Controller, useFormContext } from "react-hook-form";
import type { CountryData } from "react-phone-input-2";
import { Country, getCountryCode, isUsMarket } from "utils/market";

import { PhoneNumberInput, toE164 } from "./PhoneNumberInput";

interface Props {
  country?: string;
  defaultValue?: string;
  withCountryCodeField?: boolean;
  containerStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}

// Registers phone_number ("" until the input holds more than the dial code, E.164 otherwise)
// and phone_country_code following the selected country. Pair with withOptionalPhone at submit
// so an untouched or cleared field never sends an empty string to the backend.
export const PhoneNumberField: React.VFC<Props> = ({
  country,
  defaultValue = "",
  withCountryCodeField = true,
  ...styles
}) => {
  const { control, setValue } = useFormContext();
  const defaultCountry = country ?? getCountryCode(isUsMarket ? Country.US : Country.SE).toLowerCase();

  return (
    <>
      <Controller
        name="phone_number"
        control={control}
        defaultValue={defaultValue}
        render={({ value }) => (
          <PhoneNumberInput
            country={defaultCountry}
            value={value}
            inputProps={{ name: "phone_number" }}
            onChange={(inputValue, data: CountryData) => {
              setValue("phone_number", !inputValue || inputValue === data.dialCode ? "" : toE164(inputValue));
              if (withCountryCodeField) {
                setValue("phone_country_code", data.countryCode.toUpperCase());
              }
            }}
            {...styles}
          />
        )}
      />
      {withCountryCodeField && (
        <Controller
          name="phone_country_code"
          control={control}
          defaultValue={defaultCountry.toUpperCase()}
          render={field => <input type="hidden" {...field} />}
        />
      )}
    </>
  );
};

export const withOptionalPhone = <T extends { phone_number?: string; phone_country_code?: string }>({
  phone_number,
  phone_country_code,
  ...rest
}: T): Omit<T, "phone_number" | "phone_country_code"> & { phone_number?: string; phone_country_code?: string } =>
  phone_number ? { ...rest, phone_number, phone_country_code } : rest;
